import ActivityReportingSettings from 'Views/Sensors/MachineActivity/ReportingSettings';
import { useState, type ReactNode } from 'react';
import { Alert, Button, Tabs, Tag } from 'antd';
import DeviceHeartbeatPanel, {
  DeviceModeEditor,
  ModeHistory,
} from '../Heartbeat/DeviceHeartbeatPanel';
import { useDeviceHeartbeats, useSaveDeviceMode } from '../Heartbeat/useDeviceHeartbeats';
import { modeLabels } from '../Heartbeat/presentation';
import type { SensorScope } from './contracts';
import { useAlertMigration } from './migrationTransport';
import MigrationStatusPanel from './MigrationStatusPanel';
import RuleManager from './RuleManager';
import IncidentOverview from './IncidentOverview';
import './unifiedAlerts.css';
import './deviceWorkspace.css';

export type DeviceWorkspaceView = 'status' | 'settings' | 'incidents';
export type AlertOwnership = 'active' | 'legacy' | 'unknown';

export default function DeviceWorkspace({
  orgId,
  sensorId,
  view,
  onViewChange,
  statusContent,
  showIdentity = true,
}: SensorScope & {
  view?: DeviceWorkspaceView;
  onViewChange?: (view: DeviceWorkspaceView) => void;
  statusContent?: ReactNode | ((ownership: AlertOwnership) => ReactNode);
  showIdentity?: boolean;
}) {
  const scope = { orgId, sensorId };
  const [localView, setLocalView] = useState<DeviceWorkspaceView>('status');
  const selected = view || localView;
  const heartbeats = useDeviceHeartbeats(scope);
  const migration = useAlertMigration(scope);
  const saveMode = useSaveDeviceMode();
  const device = heartbeats.data?.find(
    (item) => item.orgId === orgId && item.sensorId === sensorId
  );
  const confirmed = !migration.isError && !migration.isPending;
  const active = confirmed && migration.data?.migration?.status === 'active';
  const existing =
    confirmed && !active && Boolean(migration.data?.plan || migration.data?.migration);
  const ownership: AlertOwnership = active ? 'active' : existing ? 'legacy' : 'unknown';
  function select(next: string) {
    const nextView = next as DeviceWorkspaceView;
    setLocalView(nextView);
    onViewChange?.(nextView);
  }
  const settings = (
    <div className="ua-stack">
      <section className="ua-card ua-stack" aria-label="Enhedens driftsform">
        <div className="ua-header">
          <div>
            <h3>Driftsform</h3>
            <p className="ua-muted">Er enheden i drift, til test eller ikke i brug?</p>
          </div>
          {device && !heartbeats.isError && !heartbeats.isPending && (
            <div className="ua-actions">
              <Tag>{modeLabels[device.mode.mode]}</Tag>
              <DeviceModeEditor
                device={device}
                alertOwnership={ownership}
                onSave={saveMode.mutateAsync}
                onRefresh={() => void heartbeats.refetch()}
              />
            </div>
          )}
        </div>
        <p className="ua-muted">
          {active
            ? 'Live kan sende beskeder. Test opretter testhændelser uden beskeder. Ikke i brug pauser overvågningen.'
            : 'Driftsformen styrer den fælles overvågning. Eksisterende beskeder stoppes først efter overførsel og aktivering.'}
        </p>
        {heartbeats.isPending && <p role="status">Henter driftsform…</p>}
        {heartbeats.isError && (
          <Alert
            type="warning"
            message="Driftsformen kunne ikke bekræftes"
            action={<Button onClick={() => void heartbeats.refetch()}>Prøv igen</Button>}
          />
        )}
        {!heartbeats.isPending && !heartbeats.isError && !device && (
          <p>Enheden afventer første datakontrol. Driftsformen kan endnu ikke ændres her.</p>
        )}
        <ModeHistory scope={scope} />
      </section>
      {device?.sensorType === 'MACHINE_ACTIVITY' && <ActivityReportingSettings {...scope} />}
      {active ? (
        <>
          <RuleManager scope={scope} />
          <details className="ua-card device-setup-history">
            <summary>{migration.data?.migration?.origin === 'native' ? 'Første alarmopsætning' : 'Overført opsætning og modtagere'}</summary>
            <MigrationStatusPanel {...scope} showRulesLink={false} />
          </details>
        </>
      ) : (
        <>
          <MigrationStatusPanel {...scope} showRulesLink={false} />
          <details className="ua-card device-setup-history">
            <summary>Regler i den fælles overvågning</summary>
            <p className="ua-muted">
              Reglerne erstatter først de eksisterende alarmer efter aktivering. Gennemgå
              opsætningen ovenfor, før du ændrer overførte regler.
            </p>
            <RuleManager scope={scope} />
          </details>
        </>
      )}
    </div>
  );
  return (
    <section className="unified-alerts device-workspace" aria-label="Enhedens overvågning">
      {showIdentity && (
        <div className="ua-header device-workspace-identity">
          <div>
            <h2>{device?.sensorTitle || 'Valgt enhed'}</h2>
            <p className="ua-muted">{device?.orgTitle || `${orgId} · ${sensorId}`}</p>
          </div>
        </div>
      )}
      <div className={`device-alert-owner ${active ? 'is-active' : 'is-pending'}`}>
        <strong>
          {active
            ? 'Alarmstyring: samlet opsætning'
            : existing
            ? 'Alarmstyring: eksisterende opsætning'
            : 'Alarmstyring: ikke bekræftet'}
        </strong>
        <p>
          {active
            ? 'Driftsform og alarmregler ændres under Indstillinger.'
            : existing
            ? 'Eksisterende alarmer kan stadig sende beskeder, også i Test og Ikke i brug. Overfør og aktivér opsætningen under Indstillinger.'
            : 'Aktive alarmer kan ikke bekræftes endnu. Test og Ikke i brug kan ikke bekræftes at stoppe eksisterende beskeder.'}
        </p>
        {migration.isError && (
          <Button size="small" onClick={() => void migration.refetch()}>
            Hent alarmstatus igen
          </Button>
        )}
      </div>
      <Tabs
        activeKey={selected}
        onChange={select}
        destroyInactiveTabPane
        items={[
          {
            key: 'status',
            label: 'Status',
            children: (
              <>
                <DeviceHeartbeatPanel {...scope} />
                {typeof statusContent === 'function' ? statusContent(ownership) : statusContent}
              </>
            ),
          },
          { key: 'settings', label: 'Indstillinger', children: settings },
          {
            key: 'incidents',
            label: 'Hændelser',
            children: <IncidentOverview scope={scope} />,
          },
        ]}
      />
    </section>
  );
}
